/**
 * **La barre de pied de l'Atelier** — SPEC-UI §3.3, le bas du croquis :
 *
 * ```
 * │ Scores : ✍️ 82 · 🎥 74 · ✂️ —                                │
 * │ Exigence : E3 « le public attend »          [⚙ accessibilité]│
 * │ Pousse chaque phrase qui tombe dans la bonne colonne…            [ ? ] │
 * ```
 *
 * Deux lignes, deux rôles, plus une troisième depuis ⟦C23 §4⟧. La première est le **tableau de
 * marche** de la production : le score de chaque étape jouée, un tiret pour celles qui restent,
 * un 🧑‍🔧 pour celles qu'on a confiées. La seconde nomme l'exigence du tour (E0-E5, SPEC-UI
 * §4.1) et ouvre l'accessibilité **sans quitter l'Atelier** (§7.3 : « accessible partout, y
 * compris dans l'Atelier »), jusqu'ici le réglage ne se joignait que depuis la chambre, et
 * sortir de l'Atelier perdait la production.
 *
 * ⟦C23 §4⟧ **La troisième** : retour de Matt, « 3 pop-ups d'explications pour chaque nouveau
 * mini-jeu, beaucoup trop long », l'écran d'intro complet (`Atelier.tsx`) ne s'affiche plus
 * qu'une fois par mini-jeu. La règle en une phrase (`REGLES`, `Atelier.tsx`) reste donc lisible
 * ICI en permanence, tant qu'une étape est en cours (`regle` non nul) ; un bouton « ? » de
 * 48×48 px la rappelle en réplique de Matt (`BoiteDialogue`, C20) si le joueur l'a oubliée,
 * plutôt que de rouvrir l'écran d'intro entier.
 *
 * Rien n'est calculé ici : les scores viennent du store, l'exigence du pattern servi (ou du
 * moteur en bac à sable), la règle est celle que `Atelier.tsx` sait déjà pour l'étape courante.
 *
 * ⟦C57 · lot 8 · A-174⟧ **La règle du pied est COURTE** (`REGLES_COURTES`, quelques mots) : elle se lit
 * d'un coup d'œil, sans quitter le jeu des yeux. La phrase d'avant (`REGLES_LONGUES`) vit derrière « Les
 * dessous » de l'intro d'étape ; le « ? » fait redire à Matt sa réplique courte (`rappel`).
 */

import type { AtelierStage } from "@maxime/shared";

import { LIBELLE_EXIGENCE } from "../../atelier/core/exigence";
import { BoiteDialogue, type Interlocuteur } from "../../components/BoiteDialogue";
import { ETAPES } from "../../game/format";

/** Même hôte que `Atelier.tsx`/`Studio.tsx`/`NouvelleRun.tsx`, retapé à l'identique, jamais un
 *  import cross-écran pour une poignée de deux champs. */
const HOTE: Interlocuteur = { nom: "Matt", portrait: "/avatar/portrait-mathieu.png" };

// ⟦C62 · lot atelier · friction 7⟧ `LIBELLE_EXIGENCE` (« mode doux » … « tempo max ») vit désormais dans
// `atelier/core/exigence.ts` : les cinq mini-jeux en ont besoin pour leurs dessous, et un jeu n'importe pas un écran.

export function PiedAtelier({
  etapes,
  scores,
  deleguees,
  exigence,
  toleranceActive,
  onAccessibilite,
  regle,
  rappel,
  rappelOuvert = false,
  onOuvrirRappel,
  onFermerRappel,
}: {
  etapes: readonly AtelierStage[];
  scores: Partial<Record<AtelierStage, number | null>>;
  deleguees: Partial<Record<AtelierStage, string>>;
  /** Le niveau E du tour. */
  exigence: number;
  toleranceActive: boolean;
  onAccessibilite(): void;
  /** ⟦C23 §4⟧ La règle en une phrase de l'étape courante (`Atelier.tsx`, table `REGLES`),
   *  `null` tant qu'aucune étape n'est en cours (écran de résultat/publication). */
  regle?: string | null;
  /** ⟦C57 · lot 8⟧ Ce que Matt redit quand on clique « ? » : sa réplique courte de l'étape. Par défaut, `regle`. */
  rappel?: string | null;
  /** La bulle de rappel (Matt répète `rappel`, à défaut `regle`) est ouverte. */
  rappelOuvert?: boolean;
  /** Clic sur « ? » : demande le rappel. Absent ou `regle` nul ⇒ pas de bouton du tout. */
  onOuvrirRappel?(): void;
  /** Ferme la bulle de rappel (clic sur la boîte, elle n'a qu'une réplique à afficher). */
  onFermerRappel?(): void;
}) {
  return (
    <footer className="pied-atelier" aria-label="Scores en cours et accessibilité">
      {/* ⟦C23 §4⟧ La bulle de rappel flotte au-dessus du pied, jamais dans son flux (elle ne
          doit pas repousser les scores/l'exigence au-dessus d'elle). Une seule réplique : la
          boîte se ferme au premier clic, comme n'importe quelle réplique à une seule ligne. */}
      {rappelOuvert && regle ? (
        <div className="pied-rappel">
          <BoiteDialogue hote={HOTE} texte={rappel ?? regle} onSuite={onFermerRappel} />
        </div>
      ) : null}
      <p className="pied-scores">
        {/* ⟦C62 · friction 7⟧ Le chiffre nu (« 14 ») n'avait pas d'échelle : « sur 100 » est écrit une fois, ici. */}
        <span className="pied-libelle">Scores sur 100 :</span>
        {etapes.map((stage, i) => {
          const s = scores[stage];
          const texte =
            deleguees[stage] !== undefined ? "🧑‍🔧" : s === undefined ? "—" : s === null ? "?" : String(s);
          return (
            <span className="pied-score" key={stage} title={ETAPES[stage].nom}>
              {i > 0 ? <span className="pied-sep" aria-hidden="true">·</span> : null}
              <span aria-hidden="true">{ETAPES[stage].icone}</span>{" "}
              <strong>{texte}</strong>
            </span>
          );
        })}
      </p>
      <p className="pied-exigence">
        {/* ⟦C62 · friction 7⟧ Le code (« E0 ») est derrière « Les dessous » des écrans de consigne ; le pied dit le
            MOT, et seulement quand l'exigence a monté (⟦A-129⟧ : « légitime QUAND elle monte »). Au départ (E0), il
            n'y a rien à dire : la ligne ne montre que le bouton d'accessibilité. */}
        {exigence > 0 || toleranceActive ? (
          <span>
            {exigence > 0 ? (
              <>
                Exigence : <strong>{LIBELLE_EXIGENCE[exigence] ?? `niveau ${exigence}`}</strong>
              </>
            ) : null}
            {exigence > 0 && toleranceActive ? " · " : ""}
            {toleranceActive ? "tolérance active" : ""}
          </span>
        ) : (
          <span aria-hidden="true" />
        )}
        <button
          className="bouton discret"
          type="button"
          title="Accessibilité — tolérance de timing, son, mouvement réduit"
          onClick={onAccessibilite}
        >
          ⚙ accessibilité
        </button>
      </p>
      {/* ⟦C23 §4⟧ La règle en une phrase, permanente tant qu'une étape est en cours, reprise
          telle quelle de l'ancien écran d'intro (`REGLES[stage]`, `Atelier.tsx`), jamais
          retapée. Le bouton « ? » (48×48, cible minimale) n'apparaît qu'avec elle : pas de
          rappel sans rien à rappeler. */}
      {regle ? (
        <p className="pied-regle">
          <span>{regle}</span>
          <button
            className="bouton-rappel"
            type="button"
            title="Rappeler la règle"
            aria-label="Rappeler la règle du geste"
            onClick={onOuvrirRappel}
          >
            ?
          </button>
        </p>
      ) : null}
    </footer>
  );
}
